import TAGButton from '@aspendental/shared-components-web/lib/components/TAGButton';
import { Box, Skeleton, Typography } from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { buttonStyles } from './LocationsAndServices.styles';
import SelectLocationDialog from '@src/components/dialogs/selectLocationDialog/SelectLocationDialog';
import SelectTreatmentDialog from '@src/components/dialogs/selectTreatmentDialog/SelectTreatmentDialog';
import { HEART_LOCATION_STORAGE_KEY, LEARNING_CENTER_ID, VIRTUAL_CENTER_ID, promoPageSlugToServiceCode } from '@src/constants';
import { useGetLocations } from '@src/services/useGetLocations';
import { useGetServices } from '@src/services/useGetServices';
import { useSchedulingStore } from '@src/store';
type LocationsAndServicesProps = {
  index: number;
};
export const LocationsAndServices = ({
  index
}: LocationsAndServicesProps) => {
  const {
    currentStepIndex,
    selectedLocation,
    setSelectedLocation,
    selectedTreatmentId,
    setSelectedTreatmentId,
    returningGuest,
    setSelectedTreatmentName,
    setSelectedTreatmentCode
  } = useSchedulingStore(state => state);
  const {
    data: locations,
    isFetching: isFetchingLocations,
    error: errorLocations
  } = useGetLocations();
  const {
    data: services,
    isFetching: isFetchingServices,
    error: errorServices
  } = useGetServices(selectedLocation?.id ?? '');
  const router = useRouter();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [treatmentModalOpen, setTreatmentModalOpen] = useState(false);
  useEffect(() => {
    const savedLocationIdString = localStorage.getItem(HEART_LOCATION_STORAGE_KEY);
    if (!savedLocationIdString) return;
    const savedLocationId = JSON.parse(savedLocationIdString) as string;
    if (!savedLocationId) return;
    const foundLocation = locations?.find(location => location.id === savedLocationId);
    if (foundLocation) {
      setSelectedLocation(foundLocation);
    }
  }, [setSelectedLocation, locations]);

  // reads the local storage for the facilityCode and preselects the location
  useEffect(() => {
    const localStorageFc = localStorage.getItem('facilityCode');
    const formattedFc = localStorageFc?.match(/CH_?(\d+)/)?.[1] ?? localStorageFc?.replace(/"/g, '');
    if (formattedFc) {
      const preSelectedLocation = locations?.find(location => location.code === formattedFc);
      if (preSelectedLocation) {
        setSelectedLocation(preSelectedLocation);
      }
    }
  }, [locations, setSelectedLocation]);
  useEffect(() => {
    const {
      fc
    } = router.query;
    if (!fc || !locations) return;
    const facilityCode = (fc as string).toUpperCase();
    if (!facilityCode.startsWith('CH_')) return;
    const preSelectedLocation = locations.find(location => location.code === facilityCode.replace('CH_', ''));
    if (preSelectedLocation) {
      setSelectedLocation(preSelectedLocation);
    }
  }, [router.query, locations, setSelectedLocation]);

  // Filter valid locations
  const validLocations = locations?.filter(location => location.id !== VIRTUAL_CENTER_ID && location.id !== LEARNING_CENTER_ID);
  const locationName = validLocations?.find(location => location.id === selectedLocation?.id)?.display_name || 'Select a location';
  const treatments = useMemo(() => {
    if (!services) {
      return [];
    }
    const servicesTemp = [...services];
    const promoPageSlug = router.query.slug?.[0];
    if (promoPageSlug) {
      const promoServiceCode = returningGuest ? promoPageSlugToServiceCode[promoPageSlug]?.split('-')[0] : promoPageSlugToServiceCode[promoPageSlug]?.split('-')[1];
      const promoService = servicesTemp.find(service => service.code === promoServiceCode);
      if (promoService) {
        const promoServiceIndex = servicesTemp.indexOf(promoService);
        servicesTemp.splice(promoServiceIndex, 1);
        servicesTemp.unshift(promoService);
        setSelectedTreatmentId(promoService.id);
        setSelectedTreatmentName(promoService.catalog_info.display_name);
        setSelectedTreatmentCode(promoService.code);
      }
    }
    return returningGuest ? servicesTemp : servicesTemp.filter(service => service.code !== 'ONLINELHRCONSULTTX');
  }, [returningGuest, router.query.slug, services, setSelectedTreatmentId, setSelectedTreatmentName, setSelectedTreatmentCode]);
  const treatmentName = treatments?.find(treatment => treatment.id === selectedTreatmentId)?.catalog_info.display_name || 'Select a treatment';
  const disabledMode = currentStepIndex >= index;
  if (errorLocations || errorServices) {
    return <Typography variant="bodyMediumBook"> Oops! Please refresh the page and try again. </Typography>;
  }
  if (!locations || isFetchingLocations) {
    return <>
				<Skeleton variant="rounded" height={50} sx={{
        marginBottom: '1rem'
      }} />
				<Skeleton variant="rounded" height={50} />
			</>;
  }
  return <>
			<Box mt={1} display="flex" flexDirection="column" gap={2} data-sentry-element="Box" data-sentry-source-file="LocationsAndServices.tsx">
				<TAGButton variant="secondaryDefault" onClick={() => setLocationModalOpen(true)} sx={{
        ...buttonStyles,
        opacity: disabledMode ? 1 : 0.5
      }} size="S" dataTestId="location-button" data-sentry-element="TAGButton" data-sentry-source-file="LocationsAndServices.tsx">
					<Box display="flex" alignItems="center" gap={0.5} p={0.5} data-sentry-element="Box" data-sentry-source-file="LocationsAndServices.tsx">
						<Typography component="span" variant="bodyMediumBook" data-test-id="location-name" data-sentry-element="Typography" data-sentry-source-file="LocationsAndServices.tsx">
							{locationName}
						</Typography>
						<Typography variant="bodyMediumBook" lineHeight={1} data-sentry-element="Typography" data-sentry-source-file="LocationsAndServices.tsx">
							<ArrowDropDownIcon data-sentry-element="ArrowDropDownIcon" data-sentry-source-file="LocationsAndServices.tsx" />
						</Typography>
					</Box>
				</TAGButton>

				{isFetchingServices ? <Skeleton variant="rounded" width={'100%'} height={50} /> : <TAGButton disabled={!selectedLocation} variant="secondaryDefault" onClick={() => setTreatmentModalOpen(true)} sx={{
        ...buttonStyles,
        opacity: disabledMode ? 1 : 0.5
      }} size="S" dataTestId="treatment-button">
						<Box display="flex" alignItems="center" gap={0.5} p={0.5}>
							<Typography component="span" variant="bodyMediumBook" data-test-id="treatment-name">
								{treatmentName}
							</Typography>
							<Typography lineHeight={1} variant="bodyMediumBook">
								<ArrowDropDownIcon />
							</Typography>
						</Box>
					</TAGButton>}
			</Box>

			<SelectLocationDialog open={locationModalOpen} handleClose={() => setLocationModalOpen(false)} locations={validLocations} dataTestId="select-location-dialog" preSelectedLocation={selectedLocation} data-sentry-element="SelectLocationDialog" data-sentry-source-file="LocationsAndServices.tsx" />

			<SelectTreatmentDialog open={treatmentModalOpen} handleClose={() => setTreatmentModalOpen(false)} treatments={treatments!} dataTestId="select-treatment-dialog" data-sentry-element="SelectTreatmentDialog" data-sentry-source-file="LocationsAndServices.tsx" />
		</>;
};